import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DecideExtensionDto,
  DecideWarrantDto,
  EntryWarrantDetail,
  EntryWarrantStatus,
  ExecuteWarrantDto,
  FileWarrantDto,
  ResolveObstructionDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';
import { filesKeys } from './files-api';
import { deadlinesKeys } from './deadlines-keys';

export async function listWarrants(fileId: string): Promise<EntryWarrantDetail[]> {
  const { data } = await api.get<EntryWarrantDetail[]>(`/files/${fileId}/entry-warrants`);
  return data;
}

export const warrantKeys = {
  forFile: (fileId: string) => ['warrants', fileId] as const,
};

/** A warrant is "open" while it still holds the main workflow paused (brief §6). */
const OPEN_STATUSES: EntryWarrantStatus[] = ['filed', 'granted', 'extension_pending'];

export function isWarrantOpen(warrant: EntryWarrantDetail): boolean {
  return OPEN_STATUSES.includes(warrant.status);
}

/** The chronological warrant log for a file (drives the warrant-log widget). */
export function useWarrants(fileId: string | undefined) {
  return useQuery({
    queryKey: warrantKeys.forFile(fileId ?? ''),
    queryFn: () => listWarrants(fileId as string),
    enabled: !!fileId,
  });
}

/**
 * Every warrant write moves the sub-flow's state machine *and* the parent file:
 * filing pauses the file, and reaching a terminal state resumes it. So each
 * mutation invalidates the warrant list and the file detail (the status chip,
 * the stage rail and the pause banner all read the file).
 */
function useWarrantMutation<TVars>(
  fileId: string,
  fn: (vars: TVars) => Promise<EntryWarrantDetail>,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: warrantKeys.forFile(fileId) });
      qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      // Warrant follow-up timers are deadlines too.
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      toast.success('gov.warrant.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.warrant.saveError'),
  });
}

/** W1 — file the application with Provincial Court (pauses the main workflow). */
export function useFileWarrant(fileId: string) {
  return useWarrantMutation(fileId, async (dto: FileWarrantDto) => {
    const { data } = await api.post<EntryWarrantDetail>(`/files/${fileId}/entry-warrants`, dto);
    return data;
  });
}

/** Court decision on the filed application — granted (with expiry) or denied. */
export function useDecideWarrant(fileId: string) {
  return useWarrantMutation(fileId, async ({ id, dto }: { id: string; dto: DecideWarrantDto }) => {
    const { data } = await api.post<EntryWarrantDetail>(`/entry-warrants/${id}/decision`, dto);
    return data;
  });
}

/** W2 — record the execution attempt: entry occurred, or the officer was obstructed. */
export function useExecuteWarrant(fileId: string) {
  return useWarrantMutation(fileId, async ({ id, dto }: { id: string; dto: ExecuteWarrantDto }) => {
    const { data } = await api.post<EntryWarrantDetail>(`/entry-warrants/${id}/execution`, dto);
    return data;
  });
}

/** Resolve an obstruction: re-attempt with police, apply for an extension, or close. */
export function useResolveObstruction(fileId: string) {
  return useWarrantMutation(
    fileId,
    async ({ id, dto }: { id: string; dto: ResolveObstructionDto }) => {
      const { data } = await api.post<EntryWarrantDetail>(
        `/entry-warrants/${id}/obstruction-resolution`,
        dto,
      );
      return data;
    },
  );
}

/** Court decision on a pending extension — granted (new expiry) or denied. */
export function useDecideExtension(fileId: string) {
  return useWarrantMutation(
    fileId,
    async ({ id, dto }: { id: string; dto: DecideExtensionDto }) => {
      const { data } = await api.post<EntryWarrantDetail>(
        `/entry-warrants/${id}/extension-decision`,
        dto,
      );
      return data;
    },
  );
}
